/**
 * IDs — ULID partout (ou UUID v4 en environnement sans `ulid`).
 *
 * Pourquoi ULID :
 * - 26 chars (vs 36 pour UUID)
 * - Triable lexicographiquement = ordre chronologique
 * - 80 bits d'entropie (suffisant)
 * - Lisible en log
 *
 * Pourquoi ce fallback : la lib `ulid` npm dépend de `node:crypto.randomBytes`.
 * En Cloudflare Workers ce module n'est pas exposé, on génère donc un UUID v4
 * via Web Crypto (crypto.randomUUID est dispo dans le runtime Workers depuis 2022).
 * Format retourné : 36 chars hex, séparés par des tirets.
 * Conséquence : la "triabilité" lexicographique des ULID est perdue — les IDs
 * restent globalement uniques mais leur ordre ne reflète plus la date de création.
 * Acceptable pour cette itération ; à re-mesurer si on a besoin d'index trié.
 */

import { ulid } from 'ulid';

/** Génère un nouvel ID ULID. */
export const newId = (): string => {
  try {
    return ulid();
  } catch {
    // `ulid` plante dans certains runtimes (Cloudflare Workers) car elle
    // dépend de `node:crypto.randomBytes` qui n'y est pas exposé.
    // Fallback : UUID v4 via Web Crypto (dispo dans Workers depuis 2022).
    // Perte de la triabilité lexicographique, conservée pour le test 78 tests.
    return (globalThis.crypto as Crypto).randomUUID();
  }
};

/** Génère un ID "déterministe" pour les tests (à partir d'un seed). */
export const newIdFromSeed = (seed: number): string => {
  // Timestamp (10 chars) + random (16 chars). Pour les tests on fige le random.
  const ts = seed.toString(36).padStart(10, '0').toUpperCase().slice(0, 10);
  const rnd = '0000000000000000'.split('').map((_, i) =>
    ((seed * (i + 1)) % 36).toString(36),
  ).join('').toUpperCase().slice(0, 16);
  return ts + rnd;
};
